import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DE, SE, GB, FI, DK, NO, IS } from 'country-flag-icons/react/3x2';
import { Box, IconButton, List, ListItemText, Popover, Tooltip, styled } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import { useTranslation } from 'react-i18next';
import { useSettingsContext } from 'src/core/settings/useSettings';
import { DEFAULT_INPUT_LANGUAGE } from 'src/core/settings/SettingsContext';
import internationalization from 'src/shared/i18n/i18n';

export const LanguageIdSetting: React.FC<LanguageIdSettingProps> = ({ settingKey = 'InputLanguage', allowedLanguageIds, tooltip, }) => {
  const { t } = useTranslation();
  const { settings, setSetting } = useSettingsContext();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState(false);

  const languages = useMemo(() => {
    if (!allowedLanguageIds?.length) return ALL_LANGUAGES;
    return ALL_LANGUAGES.filter(language => allowedLanguageIds.includes(language.id));
  }, [allowedLanguageIds]);

  const fallbackLanguageId = languages[0]?.id ?? getDefaultLanguageId(settingKey);

  const currentSettingValue = Number((settings as any)?.[settingKey] ?? getDefaultLanguageId(settingKey) ?? fallbackLanguageId);

  const [languageId, setLanguageId] = useState<number>(currentSettingValue);

  useEffect(() => {
    if (settingKey === 'DisplayLanguage') {
      const selectedFromI18n = getLanguageFromCode(internationalization.language, languages);
      if (selectedFromI18n) {
        setLanguageId(selectedFromI18n.id);
        return;
      }
    }
    setLanguageId(currentSettingValue);
  }, [currentSettingValue, settingKey, languages]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const switchLanguage = ({ id }: { id: number }) => {
    setLanguageId(id);
    setSetting?.(settingKey, id);
    if (settingKey === 'DisplayLanguage') {
      internationalization.changeLanguage(getI18nLanguageCode(id));
    }
  };

  const selectedLanguage =
    languages.find(language => language.id === Number(languageId)) ??
    languages.find(language => language.id === 2) ??
    languages[0];

  const Flag = ({ id }: { id: number }) => {
    const language = languages.find(item => item.id === Number(id)) ?? selectedLanguage;
    if (!language) return null;
    const FlagComponent = language.flag;
    return <FlagComponent title={language.title} />;
  };

  return (
    <>
      <Tooltip arrow title={tooltip ?? t(settingKey === 'DisplayLanguage' ? 'Display Language' : 'Input Language')}>
        <IconButtonWrapper color="primary" ref={ref} onClick={handleOpen}>
          <Flag id={languageId} />
        </IconButtonWrapper>
      </Tooltip>
      <Popover disableScrollLock anchorEl={ref.current} onClose={handleClose} open={isOpen} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ maxWidth: 240 }}>
          <List sx={{ p: 0, svg: { width: 26, mr: 1 } }} component="nav">
            {languages.map(language => {
              const FlagComponent = language.flag;

              return (
                <ListItemButton key={language.id} selected={language.id === languageId} onClick={() => { switchLanguage({ id: language.id }); handleClose(); }}>
                  <FlagComponent title={language.title} />
                  <ListItemText sx={{ pl: 1 }} primary={language.title} />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Popover>
    </>
  );
};

const IconButtonWrapper = styled(IconButton)(
  ({ theme }) => `
    width: ${theme.spacing(7)};
    height: ${theme.spacing(5)};

    svg {
      width: 30px;
    }
  `
);

const DEFAULT_DISPLAY_LANGUAGE = 1;

const ALL_LANGUAGES: LanguageOption[] = [
  { id: 1, title: 'English', flag: GB, i18nCode: 'en' },
  { id: 2, title: 'Swedish', flag: SE, i18nCode: 'se' },
  { id: 3, title: 'Finnish', flag: FI },
  { id: 4, title: 'Danish', flag: DK },
  { id: 5, title: 'Norwegian', flag: NO },
  { id: 6, title: 'Icelandic', flag: IS },
  { id: 7, title: 'German', flag: DE, i18nCode: 'de' },
];

const getDefaultLanguageId = (settingKey: string) => {
  if (settingKey === 'DisplayLanguage') return DEFAULT_DISPLAY_LANGUAGE;
  return DEFAULT_INPUT_LANGUAGE;
};

const getI18nLanguageCode = (languageId?: number) => {
  switch (Number(languageId)) {
    case 2:
      return 'se';
    case 7:
      return 'de';
    case 1:
    default:
      return 'en';
  }
};

const getLanguageFromCode = (languageCode?: string, languages?: LanguageOption[]) => {
  const normalized = String(languageCode || '').toLowerCase();
  if (normalized === 'se') return languages?.find(language => language.id === 2);
  if (normalized === 'de') return languages?.find(language => language.id === 7);
  return languages?.find(language => language.id === 1);
};

export default LanguageIdSetting;

type LanguageOption = {
  id: number;
  title: string;
  flag: React.ElementType;
  i18nCode?: string;
};

type LanguageIdSettingProps = {
  settingKey?: string;
  allowedLanguageIds?: number[];
  tooltip?: string;
};
